import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { CommonLookupServiceProxy } from './service-proxies';

/**
 * Caches the Legacy GetIPInfo lookup for the lifetime of the page load.
 *
 * Several components resolve the visitor's country independently, and more than one of them can be
 * mounted at once - the home page renders AnonymousUserPageComponent with SubscriptionPlansComponent
 * embedded in its template, so each page load fired GetIPInfo twice. The Legacy IPinfo API has a
 * hard monthly usage cap, so duplicate lookups cost real quota.
 *
 * Callers keep their own handling of the response; this only guarantees a single request. Errors are
 * deliberately not swallowed, so every existing call site sees the same failure shape it does today.
 */
@Injectable({ providedIn: 'root' })
export class IpInfoService {

    private cached$: Observable<any> | null = null;

    constructor(private readonly _commonLookupServiceProxy: CommonLookupServiceProxy) { }

    /**
     * Resolves the visitor's IP info, issuing at most one HTTP request per page load. Late
     * subscribers - a modal opened minutes later - replay the already-resolved result.
     */
    getIpInfo(): Observable<any> {
        if (!this.cached$) {
            // shareReplay(1) keeps refCount false, so the result survives every subscriber
            // unsubscribing. On failure it drops the buffer, which lets the next caller retry
            // rather than inheriting a permanently failed lookup.
            this.cached$ = this._commonLookupServiceProxy.getIpInfo().pipe(shareReplay(1));
        }
        return this.cached$;
    }
}
